import React from "react";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import MovementCoachBubble from "./MovementCoachBubble";
import { entityId, formatMoney, safeNumber } from "../../../utils/erp";

function registerName(registers, registerId) {
  return registers.find((register) => entityId(register) === registerId)?.name || "Caja seleccionada";
}

function impactTone({ decision, paid, pending }) {
  if (decision === "undecided") return "warning";
  if (paid <= 0) return "warning";
  if (pending > 0) return "info";
  return "success";
}

export default function MovementImpactPreview({
  kind,
  total,
  paid,
  pending,
  payments = [],
  registers = [],
  decision = "undecided",
  stockUnits = 0,
}) {
  const physicalCash = payments
    .filter((payment) => payment.method === "cash" && payment.cashRegisterId)
    .reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
  const accountingCash = payments
    .filter((payment) => payment.method === "cash" && !payment.cashRegisterId)
    .reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
  const otherPayments = Math.max(0, paid - physicalCash - accountingCash);
  const cashNames = [...new Set(
    payments
      .filter((payment) => payment.method === "cash" && payment.cashRegisterId && safeNumber(payment.amount) > 0)
      .map((payment) => registerName(registers, payment.cashRegisterId)),
  )];
  const isPurchase = kind === "purchase";
  const isSale = kind === "sale";
  const isExpense = kind === "expense";

  let title = "Revisá el impacto antes de confirmar";
  let message = "El sistema aplicará stock, caja y contabilidad recién cuando confirmes el movimiento.";
  if (decision === "undecided") {
    title = "Falta decidir cómo se pagó";
    message = "Elegí pagado total, pago manual o dejar a cuenta. Así el sistema sabrá si debe mover dinero o crear un saldo pendiente.";
  } else if (paid <= 0 && total > 0) {
    title = isSale ? "La venta quedará pendiente de cobro" : "No saldrá dinero de caja";
    message = isSale
      ? `Los ${formatMoney(total)} quedarán en cuentas por cobrar.`
      : `Los ${formatMoney(total)} quedarán en cuentas por pagar porque no hay ningún importe abonado.`;
  } else if (pending > 0) {
    title = "Se registrará un pago parcial";
    message = `${formatMoney(paid)} se registran como pagados y ${formatMoney(pending)} quedan pendientes.`;
  } else if (isPurchase || isExpense) {
    title = isExpense ? "El gasto se registrará como pagado" : "La compra se registrará como pagada";
    message = `${formatMoney(paid)} saldrán por los medios elegidos y no se generará deuda pendiente.`;
  } else if (isSale) {
    title = "La venta se registrará como cobrada";
    message = `${formatMoney(paid)} ingresarán por los medios elegidos y no quedará saldo por cobrar.`;
  }

  return (
    <Stack spacing={1.4}>
      <MovementCoachBubble title={title} tone={impactTone({ decision, paid, pending })}>
        {message}
      </MovementCoachBubble>

      <Box sx={{ p: 1.8, borderRadius: 2.5, border: "1px solid rgba(25,36,47,0.09)", bgcolor: "#fff" }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
          <Chip size="small" label={`Total ${formatMoney(total)}`} />
          <Chip size="small" label={`Pagado ${formatMoney(paid)}`} color={paid > 0 ? "success" : "default"} variant={paid > 0 ? "filled" : "outlined"} />
          <Chip size="small" label={`Pendiente ${formatMoney(pending)}`} color={pending > 0 ? "warning" : "success"} variant="outlined" />
        </Stack>
        <Divider sx={{ mb: 1.25 }} />
        <Stack spacing={0.75}>
          {isPurchase && stockUnits > 0 ? (
            <Typography variant="body2"><strong>Stock:</strong> ingresan {stockUnits} unidad/es.</Typography>
          ) : null}
          {physicalCash > 0 ? (
            <>
              <Typography variant="body2"><strong>Caja física:</strong> egreso de {formatMoney(physicalCash)} en {cashNames.join(", ")}.</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Si esa caja no tenía saldo inicial suficiente, su saldo esperado puede quedar negativo.
              </Typography>
            </>
          ) : null}
          {accountingCash > 0 ? (
            <Typography variant="body2"><strong>Efectivo sin caja física:</strong> {formatMoney(accountingCash)} impactan solo en contabilidad, no en el arqueo.</Typography>
          ) : null}
          {otherPayments > 0 ? (
            <Typography variant="body2"><strong>Otros medios:</strong> {formatMoney(otherPayments)}.</Typography>
          ) : null}
          {pending > 0 && (isPurchase || isExpense) ? (
            <Typography variant="body2"><strong>Cuentas por pagar:</strong> aumenta {formatMoney(pending)}.</Typography>
          ) : null}
          {pending > 0 && isSale ? (
            <Typography variant="body2"><strong>Cuentas por cobrar:</strong> aumenta {formatMoney(pending)}.</Typography>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}
